import { useCallback, useEffect, useState } from 'react';
import { monEspaceApi } from '../../api/resources';
import { BonAchatA4 } from '../../components/BonAchatA4.jsx';
import { PhotoBtn } from '../../components/PhotoBtn.jsx';
import { SignaturePad } from '../../components/SignaturePad.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { _inp } from '../../components/ui/Inputs.jsx';
import {
  estPartiel,
  nonOuverts,
  quantiteLisible,
  quantitesPrises,
  resteAAcheter,
  statutLisible,
} from '../../lib/bons-achat';
import { dateSuisse } from '../../lib/dates';
import { tr } from '../../lib/i18n';
import { prepareLeJustificatif } from '../../lib/justificatif';
import { litTexteTicket } from '../../lib/lecture-ticket';
import { texteDuJustificatif } from '../../lib/ocr-ticket';
import { C } from '../../theme/palette';

const etiquette = {
  fontSize: 10,
  fontWeight: 800,
  color: C.muted,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  marginBottom: 4,
};

/** Les boutons − et + d'une quantité, assez grands pour le doigt. */
const pas = {
  width: 34,
  height: 34,
  borderRadius: 8,
  border: '1px solid ' + C.border,
  background: '#fff',
  fontSize: 18,
  fontWeight: 900,
  cursor: 'pointer',
  lineHeight: 1,
};

/** Lien d'itinéraire vers le fournisseur, par son nom et son adresse. */
const itineraire = (b) =>
  'https://www.google.com/maps/search/?api=1&query=' +
  encodeURIComponent([b.fournisseur, b.fournisseurAdresse].filter(Boolean).join(', '));

/**
 * Onglet « 🧾 Bons d'achat » de l'employé : les bons que le bureau lui a
 * envoyés, à côté de ses heures et de son planning.
 *
 * <p>Il ouvre le bon (le bureau voit qu'il l'a vu), le montre au comptoir
 * (« Montrer le bon », la feuille A4 sans prix), puis photographie le ticket :
 * la dépense se range sur le chantier du bon et le bon passe à « Acheté ».
 *
 * @param {object} props propriétés
 * @param {object} [props.soc] société : en-tête du bon
 * @param {(n: number) => void} [props.onNonOuverts] nombre de bons pas encore ouverts
 * @param {(texte: string) => void} [props.setFlash] message bref
 * @returns {import('react').ReactElement} l'onglet
 */
export function MesBonsAchat({ soc, onNonOuverts, setFlash, maSignature, onSignature }) {
  const [bons, setBons] = useState(null);
  const [erreur, setErreur] = useState('');
  const [ouvert, setOuvert] = useState(null);
  const [feuille, setFeuille] = useState(null);
  // Le ticket en cours : photo préparée, montant lu ou tapé, TVA lue.
  const [ticket, setTicket] = useState(null);
  const [montant, setMontant] = useState('');
  const [tva, setTva] = useState(null);
  const [lecture, setLecture] = useState('');
  const [envoi, setEnvoi] = useState(false);
  // Les matériaux en rupture chez le fournisseur, cochés ✗ (rangs des lignes).
  const [manquants, setManquants] = useState([]);
  // Les quantités trouvées au comptoir, quand ce n'est pas tout (2 sacs sur
  // 5), par rang de ligne.
  const [prises, setPrises] = useState({});
  const [message, setMessage] = useState('');
  // Sa signature, à droite du bon : celle de son espace (« ✍ Signature »),
  // sinon il la dessine ici une fois et elle est gardée pour la suite.
  const [signature, setSignature] = useState('');
  const saSignature = maSignature || signature;

  const charge = useCallback(
    () =>
      monEspaceApi
        .bonsAchat()
        .then((l) => {
          setBons(l || []);
          onNonOuverts && onNonOuverts(nonOuverts(l));
          return l || [];
        })
        .catch((e) => {
          setErreur(e?.message || tr('Les bons d’achat n’ont pas pu être lus.'));
          return [];
        }),
    [onNonOuverts],
  );
  useEffect(() => {
    charge();
  }, [charge]);

  const ouvre = async (b) => {
    setOuvert(b);
    setManquants([]);
    setPrises({});
    setTicket(null);
    setMontant('');
    setTva(null);
    setLecture('');
    setMessage('');
    if (b.statut === 'ENVOYE') {
      try {
        const vu = await monEspaceApi.bonAchatVu(b.id);
        setOuvert(vu);
        charge();
      } catch {
        /* le bon reste lisible ; le bureau le verra vu à la prochaine ouverture */
      }
    }
  };

  const photo = async (fichier) => {
    setMessage('');
    try {
      setTicket(await prepareLeJustificatif(fichier));
    } catch (e) {
      setMessage(e?.message || tr('La photo du ticket n’a pas pu être jointe.'));
      return;
    }
    setLecture(tr('Lecture…'));
    try {
      const lu = litTexteTicket(await texteDuJustificatif(fichier), {
        nousMemes: [soc?.nom].filter(Boolean),
        pays: soc?.pays,
      });
      if (lu.montantTtc != null) setMontant(String(lu.montantTtc));
      setTva(
        lu.montantTva != null
          ? { montantTva: +lu.montantTva, tauxTva: lu.tauxTva.length === 1 ? lu.tauxTva[0] : null }
          : null,
      );
      setLecture(lu.montantTtc != null ? tr('✓ Ticket lu : vérifie le montant.') : '');
    } catch {
      setLecture('');
    }
  };

  const basculeManquant = (i) =>
    setManquants((m) => (m.includes(i) ? m.filter((k) => k !== i) : [...m, i]));
  // Rien de pris sur une ligne : ✗, ou sa quantité mise à 0.
  const rienPris = (i) => manquants.includes(i) || (prises[i] != null && +prises[i] === 0);
  const toutManque =
    ouvert &&
    (ouvert.lignes || []).length > 0 &&
    (ouvert.lignes || []).every((_, i) => rienPris(i));
  // La quantité prise d'une ligne, bornée à 0 ; au pas de 1.
  const changeQuantite = (i, valeur) => {
    const n = Math.max(0, Math.round((+String(valeur).replace(',', '.') || 0) * 100) / 100);
    setPrises((p) => ({ ...p, [i]: n }));
    // Remise à une quantité, la ligne n'est plus ✗.
    if (n > 0) setManquants((m) => m.filter((k) => k !== i));
  };

  const enregistre = async () => {
    const m = Math.round((Number(String(montant).replace(',', '.')) || 0) * 100) / 100;
    if (!toutManque) {
      if (!ticket) {
        setMessage(tr('Prends d’abord la photo du ticket.'));
        return;
      }
      if (!(m > 0)) {
        setMessage(tr('Indique le montant du ticket.'));
        return;
      }
      if (!saSignature) {
        setMessage(tr('Signe d’abord : tu as récupéré le matériel.'));
        return;
      }
    }
    setEnvoi(true);
    // Manquants : les lignes cochées ✗ et celles dont la quantité est à 0.
    const sansRien = (ouvert.lignes || []).map((_, i) => i).filter(rienPris);
    try {
      const b = await monEspaceApi.bonAchatAchete(
        ouvert.id,
        toutManque
          ? { manquants: sansRien }
          : {
              manquants: sansRien,
              quantitesPrises: quantitesPrises(ouvert.lignes, prises),
              montant: m,
              justificatif: ticket.piece,
              justificatifNom: ticket.pieceNom,
              tauxTva: tva?.tauxTva ?? null,
              montantTva: tva?.montantTva ?? null,
              signature: saSignature || null,
            },
      );
      if (!maSignature && signature) onSignature?.(signature);
      setOuvert(b);
      setTicket(null);
      setFlash &&
        setFlash(
          toutManque
            ? '✓ ' + tr('Le bureau sait que rien n’était disponible')
            : '✓ ' +
                (b.chantierNom ? tr('Ticket enregistré sur le chantier') : tr('Ticket enregistré')),
        );
      charge();
    } catch (e) {
      setMessage(e?.message || tr('Le ticket n’a pas pu être enregistré. Réessaie.'));
    } finally {
      setEnvoi(false);
    }
  };

  if (feuille) return <BonAchatA4 bon={feuille} soc={soc} onClose={() => setFeuille(null)} />;

  if (ouvert) {
    const b = ouvert;
    return (
      <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Btn sm ghost label={'‹ ' + tr('Mes bons d’achat')} onClick={() => setOuvert(null)} />
        <Card style={{ padding: '12px 13px' }}>
          <div style={{ fontSize: 15, fontWeight: 900 }}>
            🧾 {tr('Bon d’achat')} {b.numero}
          </div>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 3 }}>
            {b.etabliPar ? tr('Envoyé par') + ' ' + b.etabliPar : ''}
            {b.dateBon ? ' · ' + dateSuisse(b.dateBon) : ''}
          </div>
        </Card>
        <Card style={{ padding: '12px 13px' }}>
          <div style={etiquette}>{tr('Où acheter')}</div>
          <div style={{ fontSize: 15, fontWeight: 800 }}>{b.fournisseur}</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>
            {b.fournisseurAdresse ? b.fournisseurAdresse + ' · ' : ''}
            <a
              href={itineraire(b)}
              target="_blank"
              rel="noreferrer"
              style={{ color: C.teal, fontWeight: 800 }}
            >
              📍 {tr('Itinéraire')}
            </a>
          </div>
        </Card>
        <Card style={{ padding: '12px 13px' }}>
          <div style={etiquette}>
            {b.chantierNom ? tr('Pour le chantier') : tr('Pour le magasin')}
          </div>
          <div style={{ fontSize: 15, fontWeight: 800 }}>
            {b.chantierNom || tr('Le stock du magasin')}
          </div>
          {b.chantierAdresse && (
            <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>{b.chantierAdresse}</div>
          )}
        </Card>
        <Card style={{ padding: '12px 13px' }}>
          <div style={etiquette}>{tr('À acheter')}</div>
          {(b.lignes || []).map((l, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 10,
                padding: '7px 0',
                borderBottom: '1px dashed ' + C.border,
                fontSize: 13.5,
              }}
            >
              <span
                style={
                  l.etat === 'MANQUANT' || (resteAAcheter(b.statut) && rienPris(i))
                    ? { color: C.red, textDecoration: 'line-through' }
                    : undefined
                }
              >
                {l.etat === 'MANQUANT' ? '✗ ' : l.etat === 'PRIS' ? '✓ ' : ''}
                {l.designation}
              </span>
              <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {resteAAcheter(b.statut) && l.quantite != null && l.quantite !== '' ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <button
                      type="button"
                      aria-label={tr('Moins') + ' : ' + l.designation}
                      onClick={() => changeQuantite(i, (prises[i] ?? +l.quantite) - 1)}
                      style={pas}
                    >
                      −
                    </button>
                    <input
                      type="text"
                      inputMode="decimal"
                      aria-label={tr('Quantité prise') + ' : ' + l.designation}
                      value={String(prises[i] ?? +l.quantite)}
                      onChange={(e) => changeQuantite(i, e.target.value)}
                      style={{
                        width: 44,
                        height: 34,
                        textAlign: 'center',
                        fontSize: 15,
                        fontWeight: 900,
                        border:
                          '1.5px solid ' +
                          (prises[i] != null && +prises[i] !== +l.quantite ? C.amber : C.border),
                        borderRadius: 8,
                      }}
                    />
                    <button
                      type="button"
                      aria-label={tr('Plus') + ' : ' + l.designation}
                      onClick={() => changeQuantite(i, (prises[i] ?? +l.quantite) + 1)}
                      style={pas}
                    >
                      +
                    </button>
                    <span style={{ fontSize: 11, color: C.muted, minWidth: 22 }}>
                      {l.unite || ''}
                    </span>
                  </span>
                ) : (
                  <b style={{ whiteSpace: 'nowrap', color: estPartiel(l) ? C.amber : undefined }}>
                    {quantiteLisible(l)}
                  </b>
                )}
                {resteAAcheter(b.statut) && (
                  <button
                    type="button"
                    aria-label={(rienPris(i) ? tr('Manquant') : tr('Pris')) + ' : ' + l.designation}
                    onClick={() => {
                      // Remis ✓ après une quantité à 0 : la quantité demandée revient.
                      if (rienPris(i) && prises[i] != null && +prises[i] === 0) {
                        setPrises((p) => ({ ...p, [i]: undefined }));
                        setManquants((m) => m.filter((k) => k !== i));
                        return;
                      }
                      basculeManquant(i);
                    }}
                    style={{
                      minWidth: 44,
                      height: 36,
                      borderRadius: 9,
                      border: 'none',
                      fontSize: 16,
                      fontWeight: 900,
                      cursor: 'pointer',
                      background: rienPris(i) ? C.red : '#EDFAF4',
                      color: rienPris(i) ? '#fff' : '#0F6B3A',
                    }}
                  >
                    {rienPris(i) ? '✗' : '✓'}
                  </button>
                )}
              </span>
            </div>
          ))}
          {resteAAcheter(b.statut) && (
            <div style={{ fontSize: 11.5, color: C.muted, marginTop: 8, lineHeight: 1.4 }}>
              {tr('Au comptoir : touche ✓ pour mettre ✗ sur un article en rupture de stock.')}{' '}
              {tr('Il y en a moins ? Corrige la quantité avec − et +.')}
            </div>
          )}
          {b.remarque && (
            <div style={{ fontSize: 12.5, marginTop: 8, whiteSpace: 'pre-wrap' }}>
              ✍️ {b.remarque}
            </div>
          )}
        </Card>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Btn label={'📄 ' + tr('Montrer le bon')} full onClick={() => setFeuille(b)} />
          {resteAAcheter(b.statut) && toutManque ? (
            <Btn
              label={envoi ? tr('Envoi…') : '✗ ' + tr('Rien de disponible')}
              color={C.red}
              full
              disabled={envoi}
              onClick={enregistre}
            />
          ) : resteAAcheter(b.statut) ? (
            <PhotoBtn
              label={'📷 ' + tr('Photo du ticket')}
              color={C.green}
              full
              onPhoto={(_apercu, fichier) => photo(fichier)}
            />
          ) : (
            <div
              style={{
                background: '#EDFAF4',
                color: '#0F6B3A',
                borderRadius: 10,
                fontWeight: 900,
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              {tr(statutLisible(b))}
            </div>
          )}
        </div>
        {ticket && resteAAcheter(b.statut) && !toutManque && (
          <Card style={{ padding: '12px 13px' }}>
            <div style={etiquette}>📷 {tr('Ticket')}</div>
            {ticket.pieceMime?.startsWith('image') || ticket.piece?.startsWith('data:image') ? (
              <img
                src={ticket.piece}
                alt={tr('Photo du ticket')}
                style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 8, display: 'block' }}
              />
            ) : (
              <div style={{ fontSize: 12 }}>📎 {ticket.pieceNom}</div>
            )}
            {lecture && (
              <div style={{ fontSize: 11.5, color: C.teal, marginTop: 6 }}>{lecture}</div>
            )}
            <label style={{ ...etiquette, display: 'block', marginTop: 10 }}>
              {tr('Montant du ticket (CHF)')}
              <input
                aria-label={tr('Montant du ticket')}
                inputMode="decimal"
                value={montant}
                onChange={(e) => {
                  setMontant(e.target.value);
                  // Le montant corrigé garde le taux lu sur le ticket ; la TVA
                  // se recalcule sur le nouveau montant.
                  setTva((t) =>
                    t?.tauxTva != null ? { tauxTva: t.tauxTva, montantTva: null } : null,
                  );
                }}
                style={{ ..._inp, marginTop: 4, textTransform: 'none' }}
              />
            </label>
            <div style={{ ...etiquette, marginTop: 10 }}>
              ✍ {tr('Ta signature : matériel récupéré')}
            </div>
            {saSignature ? (
              <img
                src={saSignature}
                alt={tr('Ta signature')}
                style={{
                  height: 60,
                  maxWidth: '100%',
                  background: '#fff',
                  border: '1px solid ' + C.border,
                  borderRadius: 8,
                  display: 'block',
                  marginBottom: 8,
                }}
              />
            ) : (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 6, lineHeight: 1.4 }}>
                  {tr('Signe une fois : ta signature sera gardée pour tes prochains bons.')}
                </div>
                <SignaturePad aValider onSign={setSignature} />
              </div>
            )}
            <Btn
              label={envoi ? tr('Enregistrement…') : '✓ ' + tr('Enregistrer le ticket')}
              color={C.green}
              full
              disabled={envoi}
              onClick={enregistre}
            />
          </Card>
        )}
        {message && <div style={{ color: C.red, fontSize: 12, fontWeight: 700 }}>{message}</div>}
        <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.45 }}>
          {tr(
            '« Montrer le bon » ouvre la feuille à présenter au comptoir. « Photo du ticket » enregistre le ticket, et le bureau voit que l’achat est fait.',
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {erreur && <div style={{ color: C.red, fontSize: 12 }}>{erreur}</div>}
      {bons === null && !erreur && (
        <div style={{ color: C.muted, fontSize: 12 }}>{tr('Chargement…')}</div>
      )}
      {bons && bons.length === 0 && (
        <Card style={{ padding: '12px 13px' }}>
          <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5 }}>
            {tr('Aucun bon d’achat pour toi. Quand le bureau t’en envoie un, il arrive ici.')}
          </div>
        </Card>
      )}
      {(bons || []).map((b) => {
        const achete = b.statut === 'ACHETE';
        return (
          <Card
            key={b.id}
            onClick={() => ouvre(b)}
            style={{ cursor: 'pointer', padding: '12px 13px' }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 8,
                alignItems: 'center',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 900 }}>
                  {b.statut === 'ENVOYE' ? '🔴 ' : ''}
                  {b.numero} · {b.fournisseur}
                </div>
                <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
                  {b.chantierNom || tr('Magasin')}
                </div>
              </div>
              <span
                style={{
                  background: achete ? '#EDFAF4' : '#FFFBEB',
                  color: achete ? '#0F6B3A' : '#92400E',
                  borderRadius: 999,
                  padding: '4px 9px',
                  fontSize: 11,
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                }}
              >
                {tr(statutLisible(b))}
              </span>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
